<template>
  <header
    data-scroll-target="header"
    data-cy="header"
    class="d-flex justify-center d-sm-flex mx-0 layout-offset-from-top"
    :class="{ 'header-shadow': isScrolling }"
  >
    <RootGridRow class="d-flex flex-grow-1 align-center py-0">
      <template #left>
        <div class="d-flex flex-grow-1 align-center">
          <SMWorkspaceSwitch />
        </div>
      </template>
      <template #right>
        <v-container class="py-3 px-4 px-md-0">
          <v-row class="d-flex flex-grow-1">
            <v-col
              v-if="isMsTeamsPersonalApp || hideAutocompleteMobile"
              class="d-sm-none"
              :cols="2"
              :sm="0"
            >
              <SMSidenavMobileActivatorButton />
            </v-col>
            <v-col
              class="d-flex flex-grow-1 align-center justify-center"
              :cols="autocompleteCols"
              :sm="8"
            >
              <a
                v-if="hideAutocompleteMobile"
                class="d-sm-none home-logo mr-n6"
                href="/"
                @click="trackHomeClick"
              >
                <v-img
                  src="@src/assets/img/header-logo.svg"
                  :width="150"
                  :max-height="32"
                  class="d-inline-flex mt-1 mr-8"
                />
              </a>

              <div
                v-if="smAndUp || !hideAutocompleteMobile"
                class="d-flex justify-start flex-grow-1"
              >
                <SMOnboardingTooltipWrapper
                  :onboardingTooltipId="OnboardingTooltipId.HeaderSearchBar"
                  class="flex-grow-1"
                >
                  <SMAutoCompleteV2
                    :autoFocus="autoFocusAutocomplete"
                    :class="{'hide-lt-tablet': hideAutocompleteMobile }"
                    @click:back="toggleAutocomplete"
                  />
                </SMOnboardingTooltipWrapper>
              </div>
            </v-col>
            <v-col
              class="d-none d-sm-block"
              :cols="0"
              :sm="4"
            >
              <div class="d-flex align-center justify-end">
                <SMSidenavMobileActivatorButton class="d-lg-none" />
                <SMNotificationsButton class="mr-4" />
                <SMOnboardingTooltipWrapper :onboardingTooltipId="OnboardingTooltipId.UserProfileButton">
                  <SMHeaderProfileMenu />
                </SMOnboardingTooltipWrapper>
              </div>
            </v-col>
            <v-col
              v-if="hideAutocompleteMobile"
              class="d-sm-none d-flex justify-end"
              :cols="2"
              :sm="0"
            >
              <SMOnboardingTooltipWrapper :onboardingTooltipId="OnboardingTooltipId.HeaderSearchBarMobile">
                <v-btn
                  v-show="hideAutocompleteMobile"
                  icon
                  variant="flat"
                  @click="toggleAutocomplete"
                >
                  <v-icon color="grey-darken-1">
                    ico-sm-search
                  </v-icon>
                </v-btn>
              </SMOnboardingTooltipWrapper>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </RootGridRow>
  </header>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import SMAutoCompleteV2 from '@src/ui/SMAutoCompleteV2/index.vue'
import SMHeaderProfileMenu from '@src/ui/SMHeaderProfileMenu/index.vue'
import SMNotificationsButton from '@src/ui/SMNotificationsButton/index.vue'
import { useOAuthClientId } from '@src/composables/useOAuthClientId'
import RootGridRow from '@src/ui/SMSidenav/RootGridRow.vue'
import SMWorkspaceSwitch from '@src/ui/SMWorkspaceSwitch/index.vue'
import SMSidenavMobileActivatorButton from '@src/ui/SMSidenav/SMSidenavMobile/SMSidenavMobileActivatorButton.vue'
import useTracking from '@src/composables/tracking/tracking'
import { useWindowScroll } from '@vueuse/core'
import SMOnboardingTooltipWrapper from '@src/ui/SMOnboardingTooltip/components/SMOnboardingTooltipWrapper.vue'
import { useDisplay } from 'vuetify'
import { OnboardingTooltipId } from '@src/types/onboardingTooltips.types'

export default defineComponent({
  name: 'HeaderComponent',
  components: {
    SMWorkspaceSwitch,
    RootGridRow,
    SMAutoCompleteV2,
    SMHeaderProfileMenu,
    SMNotificationsButton,
    SMSidenavMobileActivatorButton,
    SMOnboardingTooltipWrapper
  },
  setup() {
    const { isMsTeamsPersonalApp } = useOAuthClientId()
    const { trackMixpanelEvent } = useTracking()
    const { y } = useWindowScroll()
    const { smAndUp } = useDisplay()

    const hideAutocompleteMobile = ref(true)
    const autoFocusAutocomplete = ref(false)

    const isScrolling = computed(() => y.value > 0)
    const autocompleteCols = computed(() => (!isMsTeamsPersonalApp.value && !hideAutocompleteMobile.value ? 12 : !hideAutocompleteMobile.value && isMsTeamsPersonalApp.value ? 10 : 8))

    function toggleAutocomplete() {
      hideAutocompleteMobile.value = !hideAutocompleteMobile.value
      if (!hideAutocompleteMobile.value) {
        autoFocusAutocomplete.value = true
      }
    }
    function trackHomeClick() {
      trackMixpanelEvent('clickHome', {
        component: 'header',
        element: 'image',
        type: 'click'
      })
    }

    return {
      OnboardingTooltipId,
      isMsTeamsPersonalApp,
      hideAutocompleteMobile,
      autocompleteCols,
      autoFocusAutocomplete,
      isScrolling,
      smAndUp,
      toggleAutocomplete,
      trackHomeClick
    }
  }
})
</script>

<style lang="scss" scoped>
@use "~@styles/mixins" as mxn;

$header-height: 64px;
$sidenav-width: 240px;

header {
  background-color: #F7F7F8;
  position: fixed;
  left: 0;
  right: 0;
  height: $header-height;
  z-index: 50;
}

.header-shadow {
  box-shadow: 4px 2px 7px rgba(0, 0, 0, 0.05);
}

.header--right {
  height: 50px;
}

.home-logo {
  position: relative;
}

</style>
